/**
 * PartnerFormPopup — 시공반장님 등록 팝업
 *
 * 기존 PartnerFormPage(F-2)와 동일한 폼 구성·API 호출.
 * 등록 전용 (편집·더추가 미지원).
 *
 * 사용:
 *   import { PartnerFormPopup } from '@indipia/shared-features'
 *   <PartnerFormPopup onClose={() => setOpen(false)} onSuccess={(id) => reload()} />
 */
import { useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Checkbox, FilterChip, Segmented } from '@indipia/ui-core';
import { COMM_METHOD_OPTIONS, COMM_METHOD_HELP_CONSTRUCTION } from '@indipia/shared';
import { createPartner, fetchWorkTypes } from './api';
import type { PartnerFormPopupProps } from './types';

// ── 타입 ──────────────────────────────────────────────────────────────────────

interface FormEntry {
  partnerName: string;
  phone: string;
  selectedCodes: string[];
  hasBiz: boolean;
  commYn: boolean;         // 연락 방식 (true=알림톡 발송-회신 · false=결과만 직접등록, 기본 true)
  partnerBizNo: string;
  partnerBizName: string;
  partnerRepName: string;
}

const EMPTY_FORM: FormEntry = {
  partnerName: '',
  phone: '',
  selectedCodes: [],
  hasBiz: false,
  commYn: true,
  partnerBizNo: '',
  partnerBizName: '',
  partnerRepName: '',
};

// ── 헬퍼 ──────────────────────────────────────────────────────────────────────

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.startsWith('02')) {
    const d = digits.slice(0, 10);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
    if (d.length <= 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
  }
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function formatBizNo(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function validate(form: FormEntry): string | null {
  if (!form.partnerName.trim()) return '이름을 입력해주세요.';
  if (!form.phone.trim()) return '연락처를 입력해주세요.';
  if (form.selectedCodes.length === 0) return '전문 분야를 선택해주세요.';
  return null;
}

function toPayload(form: FormEntry) {
  return {
    partnerName: form.partnerName.trim(),
    mobile: form.phone.trim(),
    specialtyTypes: form.selectedCodes.length > 0 ? form.selectedCodes.join(',') : undefined,
    partnerBizYn: form.hasBiz ? 'Y' : 'N',
    commYn: form.commYn ? 'Y' : 'N',
    partnerBizNo: form.hasBiz && form.partnerBizNo.trim() ? form.partnerBizNo.trim() : null,
    partnerBizName: form.hasBiz && form.partnerBizName.trim() ? form.partnerBizName.trim() : null,
    partnerRepName: form.hasBiz && form.partnerRepName.trim() ? form.partnerRepName.trim() : null,
  };
}

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function PartnerFormPopup({ onClose, onSuccess }: PartnerFormPopupProps) {
  const queryClient = useQueryClient();
  const { data: workTypes = [] } = useQuery({
    queryKey: ['work-types'],
    queryFn: fetchWorkTypes,
  });

  const [form, setForm] = useState<FormEntry>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const idPrefix = useId();
  const titleId = `${idPrefix}-title`;
  const nameId = `${idPrefix}-name`;
  const phoneId = `${idPrefix}-phone`;
  const bizNoId = `${idPrefix}-bizNo`;
  const bizNameId = `${idPrefix}-bizName`;
  const bizRepNameId = `${idPrefix}-bizRepName`;

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const saveMutation = useMutation({
    mutationFn: () => createPartner(toPayload(form)),
    onSuccess: (partnerId) => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      onSuccess?.(partnerId);
      onClose();
    },
    onError: () => setFormError('저장에 실패했습니다. 다시 시도해주세요.'),
  });

  function handleSave() {
    const err = validate(form);
    if (err) { setFormError(err); return; }
    setFormError(null);
    saveMutation.mutate();
  }

  function toggleCode(code: string) {
    setForm((prev) => ({
      ...prev,
      selectedCodes: prev.selectedCodes.includes(code)
        ? prev.selectedCodes.filter((c) => c !== code)
        : [...prev.selectedCodes, code],
    }));
  }

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center"
      style={{ background: 'var(--overlay-scrim)' }}
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-surface-raised shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >

        {/* 헤더 */}
        <div className="flex items-start justify-between border-b border-border px-[22px] py-4">
          <div>
            <h2 id={titleId} className="text-base font-bold text-text">시공반장님 등록</h2>
            <p className="mt-0.5 text-[length:var(--type-caption-size)] text-text-muted">
              함께 일하는 시공반장님 정보를 등록해두면, 공사 배정이 쉬워져요
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-sm bg-transparent text-[18px] leading-none text-text-muted hover:bg-surface-subtle"
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        {/* 폼 콘텐츠 */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div>
            <p className="mb-3.5 text-xs font-bold uppercase tracking-widest text-text-muted">
              기본 정보
            </p>

            {/* 이름 */}
            <div className="border-b border-border py-3">
              <label htmlFor={nameId} className="mb-1.5 block text-xs font-semibold text-text-secondary">
                이름 <span className="text-error">*</span>
              </label>
              <Input
                id={nameId}
                value={form.partnerName}
                onChange={(e) => setForm((f) => ({ ...f, partnerName: e.target.value }))}
                placeholder="반장님 성함"
                className="max-w-none"
              />
            </div>

            {/* 연락처 */}
            <div className="border-b border-border py-3">
              <label htmlFor={phoneId} className="mb-1.5 block text-xs font-semibold text-text-secondary">
                연락처 <span className="text-error">*</span>
              </label>
              <Input
                id={phoneId}
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))}
                placeholder="010-0000-0000"
                className="max-w-none"
              />
            </div>

            {/* 전문 분야 */}
            <div className="border-b border-border py-3">
              <label className="mb-2 block text-xs font-semibold text-text-secondary">
                전문 분야 <span className="text-error">*</span>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {workTypes.map(({ workTypeCode, workTypeName }) => (
                  <FilterChip
                    key={workTypeCode}
                    active={form.selectedCodes.includes(workTypeCode)}
                    onClick={() => toggleCode(workTypeCode)}
                  >
                    {workTypeName}
                  </FilterChip>
                ))}
              </div>
            </div>

            {/* 사업자 있음 */}
            <div
              className="py-3"
              style={{ borderBottom: form.hasBiz ? '1px solid var(--shell-border)' : 'none' }}
            >
              <Checkbox
                checked={form.hasBiz}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setForm((f) => ({
                    ...f,
                    hasBiz: checked,
                    partnerBizNo: checked ? f.partnerBizNo : '',
                    partnerBizName: checked ? f.partnerBizName : '',
                    partnerRepName: checked ? f.partnerRepName : '',
                  }));
                }}
                label={
                  <span className="text-sm font-medium text-text">
                    사업자 있음{' '}
                    <span className="text-xs text-text-muted">(번호·상호·대표명 추가 입력)</span>
                  </span>
                }
              />
            </div>

            {/* 사업자 정보 (조건부) */}
            {form.hasBiz && (
              <>
                <div className="border-b border-border py-3">
                  <label htmlFor={bizNoId} className="mb-1.5 block text-xs font-semibold text-text-secondary">사업자 번호</label>
                  <Input
                    id={bizNoId}
                    value={form.partnerBizNo}
                    onChange={(e) => setForm((f) => ({ ...f, partnerBizNo: formatBizNo(e.target.value) }))}
                    placeholder="000-00-00000"
                    className="max-w-none"
                  />
                </div>
                <div className="border-b border-border py-3">
                  <label htmlFor={bizNameId} className="mb-1.5 block text-xs font-semibold text-text-secondary">상호명</label>
                  <Input
                    id={bizNameId}
                    value={form.partnerBizName}
                    onChange={(e) => setForm((f) => ({ ...f, partnerBizName: e.target.value }))}
                    placeholder="상호명 입력"
                    className="max-w-none"
                  />
                </div>
                <div className="pt-3">
                  <label htmlFor={bizRepNameId} className="mb-1.5 block text-xs font-semibold text-text-secondary">대표자명</label>
                  <Input
                    id={bizRepNameId}
                    value={form.partnerRepName}
                    onChange={(e) => setForm((f) => ({ ...f, partnerRepName: e.target.value }))}
                    placeholder="대표자명 입력"
                    className="max-w-none"
                  />
                </div>
              </>
            )}

            {/* 연락 방식 */}
            <div
              className="py-3"
              style={{ borderTop: form.hasBiz ? '1px solid var(--shell-border)' : 'none' }}
            >
              <label className="mb-1.5 block text-xs font-semibold text-text-secondary">
                연락 방식 <span className="text-error">*</span>
              </label>
              <Segmented
                value={form.commYn ? 'Y' : 'N'}
                onChange={(v) => setForm((f) => ({ ...f, commYn: v === 'Y' }))}
                options={COMM_METHOD_OPTIONS}
              />
              <p className="mt-1 text-xs text-text-muted">{COMM_METHOD_HELP_CONSTRUCTION}</p>
            </div>
          </div>

          {/* 에러 */}
          {formError && (
            <p className="mt-3 text-xs font-semibold text-error">{formError}</p>
          )}

          {/* 저장 버튼 */}
          <Button
            variant="primary"
            size="lg"
            type="button"
            onClick={handleSave}
            disabled={saveMutation.isPending}
            loading={saveMutation.isPending}
            className="mt-4 w-full"
          >
            {saveMutation.isPending ? '저장 중…' : '저장'}
          </Button>
        </div>
      </div>
    </div>
  );
}
